// react
import { useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// api
import { useDataTypesGetCollectionQuery } from '@/api/generated/recalc/endpoints/v1/data';
import {
  useGetSensorsByIdQuery,
  usePatchSensorMutation,
} from '@/api/recalc/endpoints/sensorsApiSlice';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

/**
 * Inner form — receives already-loaded data as props and initialises state
 * directly, so no useEffect is needed.
 */
const DataConversionForm = ({
  existingParams,
  dataTypeOptions,
  sensorId,
  channelNumber,
  onClose,
  t,
  dispatch,
  patchSensor,
  isSaving,
}) => {
  const [formState, setFormState] = useState({
    targetDataType: existingParams?.AnalogTargetDataType ?? '',
    conversionFactor:
      existingParams?.AnalogFactor != null
        ? String(existingParams.AnalogFactor)
        : '',
    conversionOffset:
      existingParams?.AnalogOffset != null
        ? String(existingParams.AnalogOffset)
        : '',
  });
  const { targetDataType, conversionFactor, conversionOffset } = formState;
  const setTargetDataType = (v) =>
    setFormState((s) => ({ ...s, targetDataType: v }));
  const setConversionFactor = (v) =>
    setFormState((s) => ({ ...s, conversionFactor: v }));
  const setConversionOffset = (v) =>
    setFormState((s) => ({ ...s, conversionOffset: v }));

  const hasExistingConfig =
    existingParams?.AnalogTargetDataType != null ||
    existingParams?.AnalogFactor != null ||
    existingParams?.AnalogOffset != null;

  const isBusy = isSaving;
  const canSave =
    !isBusy &&
    (targetDataType.trim() ||
      conversionFactor !== '' ||
      conversionOffset !== '');

  const handleSave = async () => {
    if (!sensorId || channelNumber == null) return;

    const saveResult = await patchSensor({
      id: sensorId,
      patch: {
        DataConversionParameters: [
          {
            ChannelNumber: channelNumber,
            TargetDataType: targetDataType.trim() || null,
            ConversionFactor:
              conversionFactor !== '' ? Number(conversionFactor) : null,
            ConversionOffset:
              conversionOffset !== '' ? Number(conversionOffset) : null,
          },
        ],
      },
    });

    if (!('error' in saveResult)) {
      dispatch(
        showSnackbar({
          message:
            t('sensors.dataConversionSaveSuccess') ||
            'Data conversion parameters saved successfully',
          severity: 'success',
        }),
      );
      onClose();
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  const handleClear = async () => {
    if (!sensorId || channelNumber == null) return;

    const clearResult = await patchSensor({
      id: sensorId,
      patch: {
        DataConversionParameters: [
          {
            ChannelNumber: channelNumber,
            TargetDataType: null,
            ConversionFactor: null,
            ConversionOffset: null,
          },
        ],
      },
    });

    if (!('error' in clearResult)) {
      dispatch(
        showSnackbar({
          message:
            t('sensors.dataConversionClearSuccess') ||
            'Data conversion parameters cleared',
          severity: 'success',
        }),
      );
      onClose();
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  return (
    <>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Typography variant="body2" color="text.secondary">
            {t(
              'sensors.dataConversionHelp',
              'Configure how the raw analog signal (channel {{channel}}) on sensor {{sensorId}} is converted to a physical measurement. The formula applied is: value = (raw × Factor) + Offset.',
              { channel: channelNumber, sensorId },
            )}
          </Typography>

          <Autocomplete
            options={dataTypeOptions}
            value={
              dataTypeOptions.find((dt) => dt.Key === targetDataType) ??
              (targetDataType ? { Key: targetDataType, Base: undefined } : null)
            }
            onChange={(_, newValue) => {
              if (typeof newValue === 'string') {
                setTargetDataType(newValue);
              } else {
                setTargetDataType(newValue?.Key ?? '');
              }
            }}
            getOptionLabel={(option) =>
              typeof option === 'string'
                ? option
                : option.Base
                  ? `${option.Key} (${option.Base})`
                  : option.Key
            }
            isOptionEqualToValue={(option, value) => option.Key === value.Key}
            freeSolo
            renderInput={(params) => (
              <TextField
                {...params}
                label={t(
                  'sensors.dataConversionTargetType',
                  'Target Data Type',
                )}
                variant="filled"
                fullWidth
                placeholder={t(
                  'sensors.dataConversionTargetTypePlaceholder',
                  'e.g. pressure, temperature',
                )}
                helperText={t(
                  'sensors.dataConversionTargetTypeHelp',
                  'The data type the converted value should be stored as',
                )}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    disableUnderline: true,
                    sx: { borderRadius: 1 },
                  },
                  htmlInput: {
                    ...params.inputProps,
                  },
                }}
              />
            )}
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label={t('sensors.dataConversionFactor', 'Conversion Factor')}
              type="number"
              value={conversionFactor}
              onChange={(e) => setConversionFactor(e.target.value)}
              placeholder="1"
              variant="filled"
              fullWidth
              helperText={t(
                'sensors.dataConversionFactorHelp',
                'Multiplier applied to raw value',
              )}
              slotProps={{
                input: {
                  // @ts-expect-error MUI step prop
                  step: 'any',
                  disableUnderline: true,
                  sx: { borderRadius: 1 },
                },
              }}
            />
            <TextField
              label={t('sensors.dataConversionOffset', 'Conversion Offset')}
              type="number"
              value={conversionOffset}
              onChange={(e) => setConversionOffset(e.target.value)}
              placeholder="0"
              variant="filled"
              fullWidth
              helperText={t(
                'sensors.dataConversionOffsetHelp',
                'Constant added after multiplication',
              )}
              slotProps={{
                input: {
                  // @ts-expect-error MUI step prop
                  step: 'any',
                  disableUnderline: true,
                  sx: { borderRadius: 1 },
                },
              }}
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {hasExistingConfig && (
          <Button
            color="error"
            variant="outlined"
            onClick={handleClear}
            loading={isBusy}
            disabled={isBusy}
            sx={{ mr: 'auto' }}
          >
            {t('sensors.dataConversionClear', 'Clear parameters')}
          </Button>
        )}
        <Button onClick={onClose} disabled={isBusy}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          loading={isBusy}
          disabled={!canSave}
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </>
  );
};

/**
 * Dialog for viewing and editing DataConversionParameters on a specific sensor channel.
 * Only relevant for analog-current / analog-voltage channels.
 *
 * @param {object}  props
 * @param {boolean} props.open          - Whether the dialog is open
 * @param {function} props.onClose      - Close callback
 * @param {string|number} props.sensorId     - Sensor ID to fetch + patch
 * @param {number}  props.channelNumber - Target channel number
 * @param {string}  props.inputName     - Human-readable input name for the title
 */
const DataConversionDialog = ({
  open,
  onClose,
  sensorId,
  channelNumber,
  inputName,
}) => {
  const { t } = useTranslation('units-gateways-sensors');
  const dispatch = useDispatch();

  const { data: sensor, isLoading: isLoadingSensor } = useGetSensorsByIdQuery(
    sensorId,
    { skip: !open || !sensorId },
  );

  const { data: dataTypes } = useDataTypesGetCollectionQuery();
  const dataTypeOptions = dataTypes ?? [];

  const [patchSensor, { isLoading: isSaving }] = usePatchSensorMutation();

  const existingParams = sensor?.SensorChannelParameters?.find(
    (p) => p.ChannelNumber === channelNumber,
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Stack spacing={1}>
          {t('sensors.dataConversionTitle', 'Data Conversion')}
          {inputName && (
            <Typography component="span" variant="body2" color="text.secondary">
              {inputName}
            </Typography>
          )}
        </Stack>
      </DialogTitle>

      {isLoadingSensor ? (
        <DialogContent dividers>
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        </DialogContent>
      ) : (
        <DataConversionForm
          key={`${sensorId}-${channelNumber}`}
          existingParams={existingParams}
          dataTypeOptions={dataTypeOptions}
          sensorId={sensorId}
          channelNumber={channelNumber}
          onClose={onClose}
          t={t}
          dispatch={dispatch}
          patchSensor={patchSensor}
          isSaving={isSaving}
        />
      )}
    </Dialog>
  );
};

export default DataConversionDialog;
